import {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  deepOrange,
  deepPurple,
  green,
  grey,
  indigo,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from '@mui/material/colors'
import { AnyAction, ThunkAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import sortBy from 'lodash/sortBy'
import { KeyboardEvent } from 'react'
import {
  updateShellPlanAttributes,
  updateUnitPlanAttributes,
} from 'src/redux/slice/MultiFamilyPlanListingSlice'
import { updatePlanAttributes } from 'src/redux/slice/plansSlice'
import { AppDispatch, RootState } from 'src/redux/stores/store'
import {
  IPlan as IPlanFilteredInterface,
  IUnit,
} from 'src/types/filterPlans.interfaces'
import { IShell } from 'src/types/filterShells.interfaces'
import { IModifiedPlan } from 'src/types/modifiedPlan.interface'
import { IShellPlan } from 'src/types/shell.interface'
import { IUnitPlan } from 'src/types/unit.interface'
import { getRegionFromDivision } from 'src/utils/commonMapper.util'
import { splitMultiValued } from 'src/utils/conversion.util'

import { vaultedCeilingLocation } from '../components/PlanEntryForms/PlanMasterData/PlanEntryFieldsData'
import {
  planModal,
  TEXAS_DIVISIONS,
  TEXAS_DIVISIONS_LABELS,
} from '../components/Plans/utils/constants'
import {
  AdditionalFacilities,
  AdditionalRooms,
  IActivePlanInfo,
  IPlanInfo,
  IPlanModalInfo,
  PlanPayload,
  PlanStructure,
  PlanType,
  SupportingDocuments,
  UserInfo,
} from '../types/plan.interface'
import { USER_PLAN_FAVORITE, USER_PLAN_REMOVE_FAVORITE } from './api-routes'
import { CancelTokenSource, instance } from './axiosInstance'
import {
  classTypes,
  documentAndSpecificationCategory,
  HIDE_UNHIDE_ACTION,
  planCategories,
  PlanPropertyKeys,
  promiseResponse,
  structureTypes,
} from './constants'
import { getEnvVar } from './env'

export const waitFor = async (ms: number = 100) => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

export const validateNumberFields = (event: KeyboardEvent) => {
  const allowedCharacters = /\d/
  if (
    !event.key.match(allowedCharacters) &&
    event.key !== 'Backspace' &&
    event.key !== 'Tab'
  ) {
    event.preventDefault()
  }
}

export const handleNumberFields = (event: KeyboardEvent) => {
  if (event.key === 'e' || event.key === 'E' || event.key === '-') {
    event.preventDefault()
  }
}

export const fileIcons: Record<string, string> = {
  csv: '/images/excel.png',
  default: '/images/image.png',
  docx: '/images/word.png',
  dwf: '/images/dwf.png',
  gif: '/images/image.png',
  jpeg: '/images/image.png',
  jpg: '/images/image.png',
  pdf: '/images/pdf.png',
  png: '/images/image.png',
  rvt: '/images/rvt.png',
  webp: '/images/image.png',
  xls: '/images/excel.png',
  xlsx: '/images/excel.png',
}

export const getFileIconPath = (fileExt?: string): string => {
  if (!fileExt) {
    return fileIcons.default
  }
  const lowerCaseExt = fileExt.toLowerCase()

  return fileIcons[lowerCaseExt as keyof typeof fileIcons] || fileIcons.default
}

export const formatDate = (date: Date, dateFormat: string): string => {
  return dayjs(date).format(dateFormat)
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency',
  }).format(amount)
}

export const formatWholeNumber = (amount: number): string => {
  return amount.toFixed(0)
}

export const formatPercentage = (percent: number): string => {
  return `${percent.toFixed(1)}%`
}

export const formatFileSize = (fileSizeInBytes: number): string => {
  const kilobytesInMegabyte = 1000
  const kilobytes = fileSizeInBytes / 1024
  const megabytes = kilobytes / kilobytesInMegabyte

  if (megabytes >= 1) {
    return megabytes.toFixed(2) + ' MB'
  } else {
    return kilobytes.toFixed(2) + ' KB'
  }
}

export const getCancelTokenSource = (): CancelTokenSource => {
  const controller = new AbortController()
  return {
    cancel: () => controller.abort(),
    token: controller.signal,
  }
}

export const addProtocolToSpecificationLinks = (
  formData: Record<string, { link?: string }[]>
) => {
  Object.keys(formData).forEach((key) => {
    formData[key] = formData[key].map((item) => {
      let link = item?.link

      if (link?.includes('http://')) {
        link = link.replace(/http/g, 'https')
      } else if (link && !link.includes('https://')) {
        link = 'https://' + link
      }

      return {
        ...item,
        link,
      }
    })
  })
}

export const sortArrayOfObjects = (array: object, sortByField: string) => {
  return sortBy(array, [
    (o) => {
      return o[sortByField]
    },
  ])
}

export const getSpecificationsColumnHeader = (linkCategory: string) => {
  const normalizedCategory =
    linkCategory === 'modsy' ? 'specifications' : linkCategory

  return documentAndSpecificationCategory[normalizedCategory]?.label ?? ''
}

export const extractPlanId = (planId: string | string[], index: number) => {
  if (typeof planId === 'string') {
    return planId
  } else {
    return planId[index]
  }
}

export const getEmailInitials = (email?: string): string => {
  const parts = email
    ?.split('@')[0]
    ?.split(/[@.-]/)
    ?.filter((part) => part?.trim())

  if (!parts || parts.length === 0) {
    return ''
  } else if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || ''
  } else {
    const initials = parts
      ?.map((part) => part[0]?.toUpperCase())
      .filter((initial) => initial)
    return initials?.join('') || ''
  }
}

export const getPlanCategory = (
  planData?: IActivePlanInfo | IModifiedPlan
): string | undefined => {
  if (!planData) {
    return undefined
  }
  if (
    PlanPropertyKeys.PLAN_OVERVIEW in planData &&
    planData[PlanPropertyKeys.PLAN_OVERVIEW]
  ) {
    return planData.planCategory.toLowerCase()
  }
  if (PlanPropertyKeys.PLAN_CATEGORY in planData) {
    return planData[PlanPropertyKeys.PLAN_CATEGORY]
  }
}

export const isCore = (planData?: IActivePlanInfo | IModifiedPlan): boolean => {
  return getPlanCategory(planData) === planCategories.CORE
}

export const isCommunity = (
  planData?: IActivePlanInfo | IModifiedPlan
): boolean => {
  return getPlanCategory(planData) === planCategories.COMMUNITY
}

/**
 * Division code(s) -> the display name(s) to show.
 *
 * GET /plan declares division a SCALAR, so a consolidated Approved Regional Plan
 * arrives with its codes comma-joined ('JAX, MFL'). A straight
 * `divisions[division.toUpperCase()]` lookup misses that key entirely and renders a
 * blank where every other plan names its division, so each code is resolved on its
 * own. A single-division plan resolves exactly as it did before; a code with no
 * mapping falls through as the raw code, matching getDivision in components/utils.
 */
export const getDivisionDisplayName = (
  division: string | undefined,
  divisions: Record<string, string>
): string =>
  splitMultiValued(division)
    .map((code) => divisions[code.toUpperCase()] ?? code)
    .join(', ')

export const getPlanName = (
  plan: {
    plan?: string
    planElevation?: string
    region?: string
    communityId?: number | string
    planCategory?: string
    division?: string
  },
  regions: Record<string, string>,
  divisions?: Record<string, string>
): string => {
  const {
    communityId = '',
    division = '',
    plan: planNumber = '',
    planElevation = '',
    region = '',
  } = plan

  // An Approved Regional Plan is division-scoped and has no community, so without
  // its own branch it fell through to `${communityId}` and named itself
  // 'Plan 3427 - A4 - ' with nothing after the last dash.
  const isDivisionScoped =
    plan.planCategory === planCategories.HIGH_VALUE_PLAN ||
    plan.planCategory === planCategories.APPROVED_REGIONAL_PLAN

  const planIdentifier = `${planNumber} - ${planElevation}`
  const category =
    plan.planCategory === planCategories.CORE
      ? regions[region as keyof typeof regions] ?? region
      : isDivisionScoped && divisions
        ? getDivisionDisplayName(division, divisions)
        : `${communityId}`

  return `Plan ${planIdentifier?.toUpperCase()} - ${category}`
}

export const getPlanActionsTextClassName = (action: string): string => {
  switch (action) {
    case 'added':
      return 'add-action'
    case 'edited':
      return 'edit-action'
    case 'removed':
      return 'remove-action'
    default:
      return ''
  }
}

export const randomColors = [
  deepPurple[500],
  teal[500],
  amber[500],
  pink[500],
  blue[500],
  green[500],
  red[500],
  purple[500],
  orange[500],
  yellow[500],
  indigo[500],
  lime[500],
  cyan[500],
  lightGreen[500],
  deepOrange[500],
  brown[500],
  grey[500],
  blueGrey[500],
]

export const isPlanCopyOrAddElevationOrPrePopulateOrCreation = () => {
  const pathName = window.location.pathname

  return [
    '/create-plan',
    '/copy-plan',
    '/copy-shell',
    '/copy-unit',
    '/add-plan-elevation',
    '/add-unit-elevation',
    '/add-shell-elevation',
    '/pre-populate-plan',
    '/pre-populate-shell',
    '/pre-populate-unit',
  ].some((keyword) => pathName.includes(keyword))
}

const breakpoint = (planInfoData: IPlanInfo) => {
  return (
    planModal.MAXIMUM_NUMBER_OF_COLUMNS_IN_GRID /
      planInfoData.planElevation?.length || 1
  )
}

export const smBreakpoint = (planInfoData: IPlanInfo) => {
  return planInfoData.planElevation?.length >
    planModal.MAXIMUM_NUMBER_OF_PLAN_ELEVATIONS_IN_STEP
    ? planModal.MAXIMUM_WIDTH_OF_ITEM_IN_GRID
    : breakpoint(planInfoData)
}

// Handle Favorites functionality start
export const handleAddToFavorites = async (
  planIds: string | string[],
  email: string,
  appDispatch: AppDispatch,
  setFavorites: (favorites: string[]) => AnyAction,
  getFavoritePlans: (
    email: string
  ) => ThunkAction<void, RootState, unknown, AnyAction>,
  handleClose: () => void = () => {}
) => {
  const isArrayPlanIds = Array.isArray(planIds)

  if (planIds) {
    const response = await instance.patch<{
      favorites: { planId: string }[]
    }>(USER_PLAN_FAVORITE, {
      planIds: isArrayPlanIds ? [...planIds] : [planIds],
      userId: email,
    })
    const { favorites } = response.data
    const userFavoritePlansIds = favorites.map(
      (item: { planId: string }) => item.planId?.toUpperCase()
    )

    appDispatch(setFavorites([...userFavoritePlansIds]))
    sessionStorage.setItem('favorites', JSON.stringify(userFavoritePlansIds))
    if (!isArrayPlanIds) handleClose()
    await appDispatch(getFavoritePlans(email))
  }
}

export const handleRemoveFromFavorites = async (
  planIds: string | string[],
  email: string,
  appDispatch: AppDispatch,
  setFavorites: (favorites: string[]) => AnyAction,
  getFavoritePlans: (
    email: string
  ) => ThunkAction<void, RootState, unknown, AnyAction>,
  handleClose: () => void = () => {},
  setUnFavoriteLoading: (loading: boolean) => void = () => {}
) => {
  const isArrayPlanIds = Array.isArray(planIds)

  if (isArrayPlanIds) setUnFavoriteLoading(true)
  if (planIds) {
    const response = await instance.patch<{
      favorites: { planId: string }[]
    }>(USER_PLAN_REMOVE_FAVORITE, {
      planIds: isArrayPlanIds ? [...planIds] : [planIds],
      userId: email,
    })

    const { favorites } = response.data
    const userFavoritePlansIds = favorites.map(
      (item: { planId: string }) => item.planId
    )

    appDispatch(setFavorites([...userFavoritePlansIds]))
    sessionStorage.setItem('favorites', JSON.stringify(userFavoritePlansIds))
    if (!isArrayPlanIds) handleClose()
    await appDispatch(getFavoritePlans(email))

    if (isArrayPlanIds) setUnFavoriteLoading(false)
  }
}
// Handle Favorites functionality end

export const planSubmissionStatus = (): boolean => {
  return !(
    window.location.pathname.includes('/draft') ||
    window.location.pathname.includes('/create-plan') ||
    window.location.pathname.includes('/copy-plan') ||
    window.location.pathname.includes('/add-plan-elevation') ||
    window.location.pathname.includes('/add-unit-elevation') ||
    window.location.pathname.includes('/add-shell-elevation')
  )
}

export const getMatchedVaultedCeilingLocation = (
  vaultedCeilingLocationLabel: string
) => {
  const matchedData = vaultedCeilingLocation.find(
    (item) => item.value === vaultedCeilingLocationLabel
  )
  return matchedData ? matchedData.label : '-'
}

export const dashboardCardTitleMapper = (key: string): string => {
  const keys = {
    COMMUNITIES_IN: 'Communities in',
    DIVISION_PLAN_STATUS: 'Division Plan Status',
    MY_DRAFTS: 'My Drafts',
    MY_PLANS: 'My Plans',
    PLAN_STATUS_ACROSS_LENNAR: 'Plan Status Across Lennar',
    PLANS_IN: 'Plans in',
    TOTAL_COMMUNITIES: 'Total Communities',
    TOTAL_CORE_PLANS: 'Total Base Core Plans',
    TOTAL_PLANS: 'Total Base Plans',
    TOTAL_VALUE_ENGINEERED_PLANS: 'Total Base Value Engineered Plans',
  }

  if (key?.includes('COMMUNITIES_IN')) {
    return `${keys.COMMUNITIES_IN} ${key.split('|')[1]}`
  } else if (key?.includes('PLANS_IN')) {
    return `${keys.PLANS_IN} ${key.split('|')[1]}`
  }

  return keys[key as keyof typeof keys]
}

export const getCommunityOrRegion = (
  plan: IShellPlan | IUnitPlan | PlanType,
  showFullRegion: boolean = false,
  regions: Record<string, string>
): string => {
  const planOverview = (plan as PlanType)?.planOverview
  const unitOverview = (plan as IUnitPlan)?.unitOverview
  const shellOverview = (plan as IShellPlan)?.shellOverview

  const isCommunityPlan =
    planOverview?.planCategory === planCategories.COMMUNITY ||
    unitOverview?.unitCategory === planCategories.COMMUNITY ||
    shellOverview?.shellCategory === planCategories.COMMUNITY

  if (isCommunityPlan) {
    return (
      planOverview?.communityId ||
      unitOverview?.communityId ||
      shellOverview?.communityId ||
      ''
    )
  }

  const region =
    (plan as PlanType)?.region || unitOverview?.region || shellOverview?.region

  if (showFullRegion && region) {
    return regions[region as keyof typeof regions] || ''
  }

  return region || ''
}

export const divisionIsInTexas = (
  divisions: IActivePlanInfo | string
): boolean => {
  if (typeof divisions !== 'string') {
    return TEXAS_DIVISIONS.includes(divisions?.divisionCode?.toLowerCase())
  }
  return TEXAS_DIVISIONS.includes(divisions.toLowerCase())
}

export const divisionIsInTexasByLabel = (divisionLabel: string): boolean => {
  return TEXAS_DIVISIONS_LABELS.includes(divisionLabel.toLowerCase())
}

export const isCoreGroveRegionPlan = (
  region?: string,
  planCategory?: string
) => {
  return (
    region?.toLowerCase() === 'gro' &&
    planCategory?.toLowerCase() === planCategories.CORE
  )
}

export const filterPlans = (planArray: IModifiedPlan[], planId: string) => {
  return planArray.map((item) => ({
    ...item,
    hidden: item.id === planId.toUpperCase() ? !item.hidden : item.hidden,
  }))
}

export const handleHideUnhidePlan = (
  planId: string,
  dispatch: AppDispatch,
  isMultiFamilyShellPlan: boolean,
  isMultiFamilyUnitPlan: boolean
) => {
  // The three thunks take the same argument but resolve to different payloads, so
  // the conditional yields a union of AsyncThunkAction that dispatch cannot accept.
  // Narrowing to one signature keeps the call typed; the payload is not read here.
  const updateFunction = (
    isMultiFamilyShellPlan
      ? updateShellPlanAttributes
      : isMultiFamilyUnitPlan
        ? updateUnitPlanAttributes
        : updatePlanAttributes
  ) as typeof updatePlanAttributes

  dispatch(
    updateFunction({
      action: HIDE_UNHIDE_ACTION,
      communityPlanId: planId,
    })
  )
}

export const getPrefixedPlanName = (structureType: string): string => {
  let planName: string = ''
  switch (structureType) {
    case structureTypes.TOWN_HOME:
      planName = 'T'
      break
    case structureTypes.CONDOMINIUM:
      planName = 'C'
      break
    case structureTypes.GARAGE:
      planName = 'GA'
      break
    case structureTypes.CARPORT:
      planName = 'CP'
      break
    default:
      planName = ''
  }

  return planName
}

export const resetTimer = (
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  saveChanges: () => void
) => {
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current)
  }
  timeoutRef.current = setTimeout(
    () => {
      saveChanges()
    },
    getEnvVar(process.env.REACT_APP_AUTO_SAVE_FREQUENCY ?? '')
      ? Number(getEnvVar(process.env.REACT_APP_AUTO_SAVE_FREQUENCY ?? ''))
      : 20000
  )
}

export function isExteriorCladdingTypeSelected(
  exteriorCladdingTypes: string[] | undefined,
  claddingType: string
): boolean {
  return exteriorCladdingTypes?.includes(claddingType) || false
}

/**
 * Checks if two documents are the same based on their properties
 */
const isSameDocument = (
  doc1: SupportingDocuments,
  doc2: SupportingDocuments
): boolean => {
  return (
    doc1.fileId === doc2.fileId ||
    (doc1.fileName === doc2.fileName && doc1.fileTab === doc2.fileTab)
  )
}

/**
 * Merges documents from primary elevation with secondary elevation documents
 * Rules:
 * - Documents that exist in secondary elevation (by fileId or fileName/fileTab) are kept as-is (preserves versions)
 * - Only NEW documents from primary elevation are added to secondary
 * - This prevents overwriting document versions that were modified in secondary elevations
 */
export const mergeDocumentsForPropagation = (
  primaryDocs: SupportingDocuments[],
  secondaryDocs: SupportingDocuments[]
): SupportingDocuments[] => {
  if (!primaryDocs || primaryDocs.length === 0) {
    return secondaryDocs || []
  }

  if (!secondaryDocs || secondaryDocs.length === 0) {
    return primaryDocs
  }

  const mergedDocs = [...secondaryDocs]
  primaryDocs.forEach((primaryDoc) => {
    const existsInSecondary = secondaryDocs.some((secondaryDoc) =>
      isSameDocument(primaryDoc, secondaryDoc)
    )

    if (!existsInSecondary) {
      mergedDocs.push(primaryDoc)
    }
  })

  return mergedDocs
}

/**
 * Merges two arrays of SupportingDocuments, deduplicating by isSameDocument.
 * New docs are added only if not already present in existingDocs.
 */
export const mergeSupportingDocuments = (
  existingDocs: SupportingDocuments[],
  newDocs: SupportingDocuments[]
): SupportingDocuments[] => {
  if (!Array.isArray(existingDocs) && !Array.isArray(newDocs)) return []
  if (!Array.isArray(existingDocs)) return newDocs || []
  if (!Array.isArray(newDocs)) return existingDocs || []
  const merged = [...existingDocs]
  newDocs.forEach((doc) => {
    if (!merged.some((d) => isSameDocument(d, doc))) {
      merged.push(doc)
    }
  })
  return merged
}

/**
 * Processes upload document responses and merges them with existing plan documents.
 * Maps upload results back to their correct elevation indices using uploadPromiseIndexMap.
 *
 * @param uploadDocumentsPromisesResponse - Array of settled promise results from document uploads
 * @param uploadPromiseIndexMap - Array mapping upload promise indices to elevation indices
 * @param planArray - Array of plans containing existing supplemental documents
 * @returns Array of merged supporting documents for each elevation
 */
export const processUploadDocumentResponses = <
  T extends { supplementalDocuments?: SupportingDocuments[] },
>(
  uploadDocumentsPromisesResponse: PromiseSettledResult<
    SupportingDocuments[]
  >[],
  uploadPromiseIndexMap: number[],
  planArray: T[]
): Array<SupportingDocuments[]> => {
  const uploadDocumentsResponses: Array<SupportingDocuments[]> = planArray.map(
    (plan) => plan?.supplementalDocuments || []
  )

  uploadDocumentsPromisesResponse.forEach((response, uploadIndex) => {
    const elevationIndex = uploadPromiseIndexMap[uploadIndex]

    if (
      typeof elevationIndex !== 'number' ||
      elevationIndex < 0 ||
      elevationIndex >= planArray.length
    ) {
      return // Skip invalid indices
    }

    const currentPlan = planArray[elevationIndex]
    const existingDocs = currentPlan?.supplementalDocuments || []

    if (response.status === promiseResponse.FULFILLED) {
      const newDocs = response.value
      if (Array.isArray(newDocs) && newDocs.length > 0) {
        uploadDocumentsResponses[elevationIndex] = mergeSupportingDocuments(
          existingDocs,
          newDocs
        )
      } else {
        uploadDocumentsResponses[elevationIndex] = existingDocs
      }
    } else {
      uploadDocumentsResponses[elevationIndex] = existingDocs
    }
  })

  return uploadDocumentsResponses
}

export const formatFieldName = (fieldId: string): string => {
  return fieldId
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
}

const fieldToPropertyMap: Record<string, keyof IPlanFilteredInterface> = {
  widthOfPlan: 'widthOfThePlanInFeet',
}

const unitFieldToPropertyMap: Record<string, keyof IUnit> = {
  conditionedSpaceSqFt: 'sqftOfUnitConditionedSpace',
  createdBy: 'planCreatedBy',
  createdOn: 'planCreatedDateTime',
  depthOfUnitInFeet: 'unitDepthFeet',
  modifiedOn: 'planModifiedDateTime',
  planCategory: 'unitCategory',
  planElevation: 'unitElevation',
  totalUnitSqFt: 'unitSqft',
  widthOfUnitInFeet: 'unitWidthFeet',
}

const shellFieldToPropertyMap: Record<string, keyof IShell> = {
  buildingDepthFt: 'buildingDepthFeet',
  buildingWidthFt: 'buildingWidthFeet',
  createdBy: 'planCreatedBy',
  createdOn: 'planCreatedDateTime',
  modifiedOn: 'planModifiedDateTime',
  planCategory: 'shellCategory',
  planElevation: 'shellElevation',
}

export const getPropertyNameFromField = (
  fieldId: string,
  classType: classTypes
): keyof IPlanFilteredInterface | keyof IUnit | keyof IShell | string => {
  switch (classType) {
    case classTypes.SINGLE:
      return (
        fieldToPropertyMap[fieldId] || (fieldId as keyof IPlanFilteredInterface)
      )

    case classTypes.UNIT:
      return unitFieldToPropertyMap[fieldId] || (fieldId as keyof IUnit)

    case classTypes.SHELL:
      return shellFieldToPropertyMap[fieldId] || (fieldId as keyof IShell)

    default:
      return ''
  }
}

export const toEnum = <T extends Record<string, string | number>>(
  enumObj: T,
  value: string
): T[keyof T] | undefined => {
  const enumValues = Object.values(enumObj) as (string | number)[]
  if (enumValues.includes(value)) {
    return value as T[keyof T]
  }
  if (value in enumObj) {
    return enumObj[value as keyof T]
  }
  return undefined
}

export const getCategoryFieldKey = (
  category: string | undefined
): keyof IPlanModalInfo => {
  switch (category) {
    case planCategories.CORE:
      return 'region'
    case planCategories.COMMUNITY:
      return 'communityNumber'
    default:
      return 'divisionCode'
  }
}

export const getCategoryFieldValue = (
  category: string | undefined,
  region: string | undefined,
  community: string | undefined,
  division: string | undefined,
  divisions: { [key: string]: string }
): string => {
  switch (category) {
    case planCategories.CORE:
      return region || ''
    case planCategories.COMMUNITY:
      return community || ''
    default:
      return (
        Object.keys(divisions).find(
          (key) => divisions[key as keyof typeof divisions] === division
        ) || ''
      )
  }
}

export const buildDraftPlanPayload = (
  draftPlanModalInfo: IPlanModalInfo,
  communityPlanId: string,
  planElevation: string,
  userInfo: UserInfo,
  regions: Record<string, string>,
  regionToDivision: Record<string, string[]>,
  divisions: Record<string, string>
): PlanPayload => {
  return {
    additionalFacilities: {} as AdditionalFacilities,
    additionalRooms: {} as AdditionalRooms,
    communityPlanId,
    createdByCorporate: false,
    division: draftPlanModalInfo.divisionCode || '',
    divisionName:
      draftPlanModalInfo.planCategory === planCategories.CORE
        ? ''
        : divisions[draftPlanModalInfo.divisionCode?.toUpperCase()] ?? '',
    garages: {},
    isCeilingHeightsRequired: true,
    isFormDirty: true,
    plan: draftPlanModalInfo.plan,
    planArea: {
      sqftOfHomeConditionedSpace: '',
      sqftOfHomeUnderRoof: '',
    },
    planCreatedBy: userInfo?.email || '',
    planCreationDetails: {
      planAction: 'update',
      planCopied: false,
      userGroups: userInfo?.groups || [],
    },
    planElevation,
    planId: communityPlanId,
    planModifiedBy: userInfo?.email || '',
    planOverview: {
      communityId: draftPlanModalInfo?.communityNumber?.[0]?.cNumberVal || '',
      communityName:
        draftPlanModalInfo.planCategory === planCategories.COMMUNITY
          ? draftPlanModalInfo?.communityNumber?.[0]?.communityName || ''
          : '',
      planCategory: draftPlanModalInfo.planCategory,
    },
    planPrice: {
      baseHomePrice: 0,
      baseHousePre: 0,
      targetProfitPerc: '',
    },
    planStructure: {} as PlanStructure,
    region:
      draftPlanModalInfo.region ||
      getRegionFromDivision(
        regionToDivision,
        regions,
        draftPlanModalInfo.divisionCode || ''
      ),
    regionName:
      regions[draftPlanModalInfo.region || ''] ||
      Object.keys(regionToDivision).find((key) =>
        regionToDivision[key].includes(
          draftPlanModalInfo.divisionCode?.toUpperCase()
        )
      ) ||
      '',
    specifications: {
      marketingMaterials: [],
      modsy: [],
      virtualTours: [],
    },
    structureSpecification: {
      architecturalShake: [],
      architecturalShingle: [],
      boardAndBatten: [],
      exteriorCladding: [],
      exteriorWallConstruction: [],
      interiorWallConstruction: [],
      materialRoofType: [],
      roofShape: [],
      sidingLap: [],
      sidingPanel: [],
    },
    supportingDocuments: [],
  }
}
